import { ProductCatalogStorefrontStatus } from '@graphql/generated-base';
import { useCallback, useMemo } from 'react';
import type { UseDimensionAvailabilityArgs } from './BuyForm.types';

export const useDimensionAvailability = ({ relatedItems, dimensionFormValues }: UseDimensionAvailabilityArgs) => {
  // Iterate over relatedItems to get a list of composite keys for each item dimension values
  const availableList: string[] = useMemo(() => {
    const notDiscontinuedItems = relatedItems?.filter(item => item?.storefrontInfo?.storefrontStatus !== ProductCatalogStorefrontStatus.discontinued);
    return (
      notDiscontinuedItems?.map(item => {
        if (!item) return '';
        // Sort dimensions by dimension id (this iteration can be avoided if the dimensions are sorted unambiguously)
        const sortedOptionsByDimension = [...item.groupDimensionOptions].sort((a, b) => (a.groupDimension.id > b.groupDimension.id ? 1 : -1));
        // Create a composite key for each dimension option combination
        return sortedOptionsByDimension.map(dim => dim.id).join('::');
      }) ?? []
    );
  }, [relatedItems]);

  // Check if the given dimension option is in the availableList
  const checkOptionAvailability = useCallback(
    (optionId: string, dimensionId: string | undefined): boolean => {
      if (!dimensionId) return false;
      // Get new set of values replacing the evaluated dimension value
      const currentFormValues = { ...dimensionFormValues, [dimensionId]: optionId };
      // Sort dimensions by dimension id (this iteration can be avoided if the dimensions are sorted unambiguously)
      const sortedOptionsByDimension = Object.keys(currentFormValues).sort();
      // Get composite key for the current dimensions combination
      const itemOptionsKey = sortedOptionsByDimension.map(dim => currentFormValues[dim as keyof typeof currentFormValues]).join('::');
      // Check if the evaluated combination exists in the available list
      return availableList.includes(itemOptionsKey);
    },
    [availableList, dimensionFormValues],
  );

  return {
    availableList,
    checkOptionAvailability,
  };
};
